/**
 * Geocoding Service for reverse geocoding coordinates to country/state
 * Uses Nominatim (OpenStreetMap) API - free, no API key required
 */

import { fetchJSON } from '../utils/fetchClient';

interface NominatimResponse {
  address?: {
    country?: string;
    country_code?: string;
    state?: string;
    province?: string;
    region?: string;
    county?: string;
  };
  error?: string;
}

export interface GeocodingResult {
  country: string;
  state: string;
}

export class GeocodingService {
  private static readonly BASE_URL = 'https://nominatim.openstreetmap.org';
  private static readonly USER_AGENT = 'StationExplorer/1.0';
  private static readonly RATE_LIMIT_MS = 1000; // Nominatim requires 1 request per second
  
  private static lastRequestTime = 0;
  private static cache = new Map<string, GeocodingResult>();

  /**
   * Reverse geocode coordinates to get country and state
   * @param latitude - Latitude coordinate
   * @param longitude - Longitude coordinate
   * @returns Country and state information
   */
  static async reverseGeocode(latitude: number, longitude: number): Promise<GeocodingResult> {
    // Check cache first
    const cacheKey = `${latitude.toFixed(4)},${longitude.toFixed(4)}`;
    const cached = this.cache.get(cacheKey);
    if (cached) {
      return cached;
    }

    // Rate limiting - ensure at least 1 second between requests
    const now = Date.now();
    const timeSinceLastRequest = now - this.lastRequestTime;
    if (timeSinceLastRequest < this.RATE_LIMIT_MS) {
      await new Promise(resolve => setTimeout(resolve, this.RATE_LIMIT_MS - timeSinceLastRequest));
    }
    this.lastRequestTime = Date.now();

    try {
      const params = new URLSearchParams({
        lat: String(latitude),
        lon: String(longitude),
        format: 'json',
        addressdetails: '1',
        zoom: '10'
      });

      const data = await fetchJSON<NominatimResponse>(
        `${this.BASE_URL}/reverse?${params}`,
        {
          headers: { 'User-Agent': this.USER_AGENT },
          signal: AbortSignal.timeout(10000)
        }
      );

      if (data.error || !data.address) {
        throw new Error('No address found');
      }

      const address = data.address;
      
      // Extract country with type validation
      const country = (typeof address.country === 'string' ? address.country : '') || '';
      
      // Extract state/province/region (different countries use different terms) with type validation
      const state = (typeof address.state === 'string' ? address.state : '') ||
                    (typeof address.province === 'string' ? address.province : '') ||
                    (typeof address.region === 'string' ? address.region : '') ||
                    (typeof address.county === 'string' ? address.county : '') || '';

      const result: GeocodingResult = {
        country,
        state: state === country ? '' : state // Don't use state if it's same as country
      };

      // Cache the result
      this.cache.set(cacheKey, result);

      return result;
    } catch (error) {
      console.warn(`⚠️ Geocoding failed for ${latitude}, ${longitude}:`, error instanceof Error ? error.message : error);
      
      // Return empty values on error
      const result: GeocodingResult = {
        country: '',
        state: ''
      };
      
      // Cache the empty result to avoid repeated failed requests
      this.cache.set(cacheKey, result);
      
      return result;
    }
  }

  /**
   * Batch reverse geocode multiple coordinates
   * Processes them sequentially to respect rate limits
   * @param coordinates - Array of {latitude, longitude} objects
   * @returns Array of geocoding results in same order
   */
  static async batchReverseGeocode(
    coordinates: Array<{ latitude: number; longitude: number }>
  ): Promise<GeocodingResult[]> {
    console.log(`🌍 Reverse geocoding ${coordinates.length} coordinates...`);
    
    const results: GeocodingResult[] = [];
    
    for (let i = 0; i < coordinates.length; i++) {
      const { latitude, longitude } = coordinates[i];
      
      if (i > 0 && i % 10 === 0) {
        console.log(`   Progress: ${i}/${coordinates.length} geocoded`);
      }
      
      const result = await this.reverseGeocode(latitude, longitude);
      results.push(result);
    }
    
    console.log(`✅ Geocoding complete: ${coordinates.length} locations processed`);
    
    return results;
  }

  /**
   * Clear the geocoding cache
   */
  static clearCache(): void {
    this.cache.clear();
  }

  /**
   * Get cache size
   */
  static getCacheSize(): number {
    return this.cache.size;
  }
}
